/**
 * PositionPicker — Visual card-based picker for riding position selection.
 * Positions are filtered based on the selected apparatus type:
 *   Engine:    IO, DPO, LFF, FF, RFF
 *   Ambulance: AIC, DO, Aide
 *   Medic:     AIC, DO, Aide
 *   Rescue:    IO, DO, LFF, FF, RFF
 *   Tower:     IO, AO, LFF, FF, RFF
 *   Brush:     IO, DPO, LFF
 *   Utility:   Operator, Aide
 *
 * Positions are fetched from API when available, falls back to shared defaults.
 */

import { useState, useEffect, useMemo } from 'react';
import { getPositionLabel, getPositionsForUnitType, POSITIONS_BY_UNIT_TYPE } from '@shared/positions';
import { apiFetch } from '../lib/api';
import { Shield, Truck, Flame, Asterisk, HeartPulse, Target, Crown, HardHat, UserCheck, Wrench } from 'lucide-react';

interface PositionPickerProps {
  selected: string | null;
  onChange: (position: string) => void;
  /** The type of the currently selected apparatus (engine, ambulance, etc.) */
  apparatusType?: string | null;
}

interface PositionOption {
  id: string;
  label: string;
}

const positionIcons: Record<string, typeof Shield> = {
  incident_officer: Shield,
  driver_pump_operator: Truck,
  driver_operator: Truck,
  aerial_operator: Target,
  aic: Asterisk,
  aide: HeartPulse,
  lead_firefighter: Flame,
  firefighter: Flame,
  recruit_firefighter: Flame,
  battalion_chief: Crown,
  safety_officer: HardHat,
  fire_chief: Crown,
  assistant_chief: Crown,
  operator: Wrench,
  fist: UserCheck,
  // Legacy IDs for API compatibility
  officer: Shield,
  driver: Truck,
  attendant: HeartPulse,
};

export default function PositionPicker({ selected, onChange, apparatusType }: PositionPickerProps) {
  const [apiOptions, setApiOptions] = useState<PositionOption[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ data: { code: string; name: string; active: number }[] }>('/api/admin/positions')
      .then((res) => {
        if (!cancelled && res.data.length > 0) {
          setApiOptions(res.data.filter((p) => p.active).map((p) => ({ id: p.code, label: p.name })));
        }
      })
      .catch(() => {
        /* Fall back to static defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter positions based on apparatus type — require a unit to be selected.
  // POSITIONS_BY_UNIT_TYPE is always the source of truth for which positions to show.
  // API data is used only for customized labels; missing positions fall back to static defaults.
  const options = useMemo(() => {
    if (!apparatusType) return [];

    const expectedIds = POSITIONS_BY_UNIT_TYPE[apparatusType];
    if (expectedIds) {
      const apiLabelMap = new Map<string, string>();
      if (apiOptions) {
        for (const opt of apiOptions) apiLabelMap.set(opt.id, opt.label);
      }
      return expectedIds.map((id) => ({
        id: id as string,
        label: apiLabelMap.get(id) ?? getPositionLabel(id),
      }));
    }

    // Unknown apparatus type — return all API options or static defaults
    if (apiOptions) return apiOptions;
    return getPositionsForUnitType(apparatusType).map((p) => ({ id: p.id as string, label: p.label as string }));
  }, [apiOptions, apparatusType]);

  return (
    <div className="w-full">
      <label className="block text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">
        Riding Position
      </label>
      {options.length === 0 ? (
        <div className="text-xs text-content-tertiary py-2">Select a unit first to see available positions.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((pos) => {
            const Icon = positionIcons[pos.id] ?? Flame;
            const isSelected = selected === pos.id;
            return (
              <button
                key={pos.id}
                type="button"
                onClick={() => onChange(pos.id)}
                className={`
                  flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium
                  transition-all duration-150 cursor-pointer border whitespace-nowrap
                  ${
                    isSelected
                      ? 'bg-action-primary/15 border-action-primary text-action-primary ring-1 ring-action-primary/30'
                      : 'bg-surface-raised border-edge-default text-content-secondary hover:border-edge-strong hover:text-content-primary'
                  }
                `}
              >
                <Icon className={`h-4 w-4 shrink-0 ${isSelected ? 'text-action-primary' : 'text-content-tertiary'}`} />
                <span>{pos.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
